import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export type ThemeName = 'base' | 'dark' | 'light'
export type ThemePreference = 'system' | 'dark' | 'light'

interface ThemeContextType {
  theme: ThemeName
  preference: ThemePreference
  setPreference: (p: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextType>(null!)

const STORAGE_KEY = 'theme_preference'

function getSystemTheme(): ThemeName {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark'
    : window.matchMedia('(prefers-color-scheme: light)').matches ? 'light'
    : 'base'
}

function loadPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'dark' || stored === 'light' || stored === 'system') return stored
  } catch {}
  return 'system'
}

function resolveTheme(pref: ThemePreference): ThemeName {
  return pref === 'system' ? getSystemTheme() : pref
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(loadPreference)
  const [theme, setTheme] = useState<ThemeName>(() => resolveTheme(loadPreference()))

  function setPreference(p: ThemePreference) {
    setPreferenceState(p)
    localStorage.setItem(STORAGE_KEY, p)
    setTheme(resolveTheme(p))
  }

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  // Keep in sync if the user changes their OS preference (only matters when preference is 'system')
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => {
      if (preference === 'system') {
        setTheme(getSystemTheme())
      }
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [preference])

  return (
    <ThemeContext.Provider value={{ theme, preference, setPreference }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => useContext(ThemeContext)
